<template>
  <AppSheet
    :is-open="isOpen"
    :mode="variant === 'chest' ? 'fullscreen' : 'centered'"
    transparent
    :fade="variant === 'chest'"
    :backdrop-dismiss="dismissible"
    :panel-class="variant === 'chest' ? 'chest-panel' : undefined"
    :backdrop-class="variant === 'chest' ? 'chest-backdrop' : undefined"
    @update:is-open="(v: boolean) => { if (!v) handleDismiss() }"
  >
    <div class="modal-wrapper" :class="{ 'chest-modal-wrapper': variant === 'chest' }">
      <!-- Header -->
      <div v-if="dialogTitle || $slots.title" class="modal-header">
        <div class="modal-header-content">
          <h2 v-if="dialogTitle" class="modal-title">{{ dialogTitle }}</h2>
          <div v-else class="modal-title-slot">
            <slot name="title" />
          </div>
          
          <p v-if="dialogDescription" class="modal-description">{{ dialogDescription }}</p>
        </div>
        
        <button
          v-if="showCloseButton"
          type="button"
          class="modal-close-btn"
          @click="handleClose"
        >
          <LucideIcon name="x" :size="20" />
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <slot />
      </div>

      <!-- Footer -->
      <div v-if="$slots.footer || showDefaultFooter" class="modal-footer">
        <slot name="footer">
          <Button
            v-if="showDefaultFooter"
            @click="handleClose"
            color-variant="outline"
            expand="block"
            class="cancel-btn"
          >
            {{ $t('app.cancel') }}
          </Button>
        </slot>
      </div>
    </div>
  </AppSheet>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AppSheet from '@/components/common/AppSheet.vue';
import { Button } from '@/components/ui/button';

interface Props {
  modelValue?: boolean;
  dialogTitle?: string;
  dialogDescription?: string;
  dismissible?: boolean;
  showDefaultFooter?: boolean;
  showCloseButton?: boolean;
  /** 'chest' = plein écran, fond transparent, backdrop flouté */
  variant?: 'default' | 'chest';
}

const props = withDefaults(defineProps<Props>(), {
  dismissible: true,
  showDefaultFooter: true,
  showCloseButton: true,
  variant: 'default'
});

/**
 * La variante coffre apparaît en FONDU, pas en montée : le décor de la grotte
 * se met en place sur lui-même, une montée le ferait glisser pendant qu'il
 * apparaît. C'est la prop `fade` d'AppSheet, un simple jeu d'images-clés CSS.
 *
 * L'ancienne version passait par une `enterAnimation` Ionic, et son
 * commentaire rappelait pourquoi la variante coffre n'avait surtout PAS de
 * breakpoints : Ionic pilote un sheet à breakpoints via progressStart, ce
 * qu'une animation personnalisée fige (PR #105 → #109). Ce piège disparaît
 * avec ion-modal.
 */

const emit = defineEmits(['update:modelValue']);

const isOpen = computed({
  get: () => props.modelValue ?? false,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

/**
 * `did-dismiss` ne se déclenche qu'une fois la modale RÉELLEMENT fermée. Le
 * modèle doit donc suivre, quoi que dise `dismissible` : cette prop décide si
 * la fermeture est AUTORISÉE (backdrop), pas si on daigne en prendre acte.
 *
 * L'ancienne version ne synchronisait que les modales dismissibles, et l'état
 * du coffre restait bloqué sur « ouvert » après une fermeture qu'elle n'avait
 * pas prévue : rouvrir ne changeait aucune prop, et plus rien ne se présentait
 * jusqu'au rechargement de l'app. Le glissé qui déclenchait ça a disparu avec
 * les breakpoints, mais le retour matériel Android ferme toujours une modale
 * sans passer par nous : la synchronisation reste indispensable.
 */
const handleDismiss = () => {
  // Rien à répercuter si le modèle est DÉJÀ à faux : la fermeture vient de
  // celui qui la pilote, il l'a donc traitée. Répercuter quand même revenait à
  // renvoyer une fermeture en retard, qui atteignait le contenu suivant quand la
  // modale avait été rouverte entre-temps.
  if (!props.modelValue) return;
  isOpen.value = false;
};

const handleClose = () => {
  isOpen.value = false;
};
</script>

<style scoped>
/* Plus de variables Ionic : AppSheet positionne, le wrapper peint. */
.modal-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.modal-wrapper.chest-modal-wrapper {
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  height: 100%;
  max-height: 100%;
  padding: 0;
  overflow: visible;
  /* L'ouverture du coffre est un fondu, porté par l'hôte. Le glissé générique
     du bas se cumulerait avec, et ferait remonter la grotte pendant qu'elle
     apparaît. */
  animation: none;
}

.modal-wrapper.chest-modal-wrapper .modal-content {
  padding: 0;
  overflow: hidden;
  height: 100%;
  min-height: 0;
}

.modal-wrapper.chest-modal-wrapper .modal-header,
.modal-wrapper.chest-modal-wrapper .modal-footer {
  background: transparent;
  border-color: transparent;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, white, transparent);
}

.modal-header-content {
  flex: 1;
  margin-right: 16px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
}

.modal-title-slot {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.modal-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.modal-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  color: #6b7280;
  margin: -4px -4px 0 0;
}

.modal-close-btn:active {
  background: rgba(0, 0, 0, 0.06);
}

.modal-content {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  min-height: 100px;
}

.modal-footer {
  padding: 16px 24px 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.cancel-btn {
  --color: #6b7280;
  --border-color: #d1d5db;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-header,
  .modal-content,
  .modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}

/* Animation d'apparition */
.modal-wrapper {
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>